import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography
} from "@mui/material";
import { pick } from "radash";
import { json, useLoaderData, useNavigate, useSearchParams, Outlet } from "react-router-dom";
import { get } from "../apiClient";
import BiddersList from "../features/programmatic_supply/BiddersList";
import useTitle from "../hooks/useTitle";
import { useScrollToTop } from "../hooks/useScrollToTop";

const PAGINATION_DEFAULTS = {
  per_page: "25",
  page: "1",
  order_direction: "desc",
  order_by: "id"
};

const searchParamsToApiParams = (searchParams) => {
  const params = Object.fromEntries(searchParams);

  return {
    ...PAGINATION_DEFAULTS,
    ...pick(params, Object.keys(PAGINATION_DEFAULTS)),
  };
};

const loader = async ({ request }) => {
  const params = searchParamsToApiParams(new URL(request.url).searchParams);

  try {
    const {
      records: bidders,
      pagination: { total_count: totalCount },
    } = await get("player", "/api/programmatic_supply/bidders", {
      params,
      signal: request.signal,
    });

    return { params, bidders, totalCount };
  } catch (error) {
    throw json(
      {
        title: "There was an error fetching bidders configuration from the server",
        text: " Please try again. If the error is persistent, contact support.",
      },
      { status: error.status }
    );
  }
};

const ProgrammaticSupplyBidders = () => {
  useTitle("Programmatic Supply Configuration > Bidder Configuration");
  useScrollToTop();
  const { params, bidders, totalCount } = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams(PAGINATION_DEFAULTS);
  const navigate = useNavigate();

  return (
    <>
      <Paper elevation={0} square={false} sx={{ borderRadius: 5 }}>
        <Box sx={{ px: 4, py: 2 }}>
          <Typography variant="h5">Bidder Configuration</Typography>
        </Box>
        <Divider />
        <Box sx={{ px: 4, pb: 2 }}>
        <Grid container sx={{ pt: 4 }}>
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ pb: 2 }}>
              <Typography variant="h6">Bidders</Typography>
            </Box>
            <Box sx={{ pb: 2 }}>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => navigate("/programmatic_supply/bidders/new")}
              >
                New Bidder
              </Button>
            </Box>
          </Grid>
        </Grid>
        <BiddersList
          onSortOrderChange={(event, order) => {
            setSearchParams((prev) => ({
              ...prev,
              order_direction: order.orderDirection,
              order_by: order.orderBy
            }))
          }}
          bidders={bidders}
          totalCount={totalCount}
          orderBy={params.order_by}
          orderDirection={params.order_direction}
          onPageChange={(_event, page) => {
            setSearchParams((prev) => ({
              ...Object.fromEntries(prev),
              page: page + 1,
            }));
          }}
          onRowsPerPageChange={(event) => {
            setSearchParams((prev) => ({
              ...Object.fromEntries(prev),
              per_page: event.target.value,
            }));
          }}
          page={Math.max(Number(searchParams.get("page")) - 1, 0)}
          rowsPerPage={Number(searchParams.get("per_page"))}
        />
        <Outlet />
        </Box>
      </Paper>
    </>
  );
};

export { loader };

export default ProgrammaticSupplyBidders;
